import { useState } from "react";
import { toast, Toaster } from "sonner";
import { ArrowRight, Instagram, Twitter, Youtube } from "lucide-react";
import Logo from "./Logo";
import Magnetic from "./Magnetic";
import Reveal from "./Reveal";

export default function ClosingCTA() {
    const [email, setEmail] = useState("");

    const onSubmit = (e) => {
        e.preventDefault();
        if (!/^\S+@\S+\.\S+$/.test(email)) {
            toast.error("Enter a valid email to reserve.");
            return;
        }
        toast.success("Reservation confirmed — welcome to the future of sound.");
        setEmail("");
    };

    return (
        <section
            id="reserve"
            data-testid="nexora-closing-cta-section"
            className="relative overflow-hidden pt-28 md:pt-40"
        >
            <Toaster
                theme="dark"
                position="bottom-center"
                toastOptions={{
                    style: {
                        background: "rgba(13,17,30,0.9)",
                        border: "1px solid rgba(0,240,255,0.25)",
                        color: "#F8FAFC",
                        backdropFilter: "blur(12px)",
                    },
                }}
            />

            {/* glow stage + ghost wordmark */}
            <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0"
            >
                <div className="absolute left-1/2 top-1/3 h-[30rem] w-[52rem] -translate-x-1/2 rounded-full bg-[radial-gradient(ellipse,rgba(0,240,255,0.1),rgba(112,0,255,0.08)_45%,transparent_70%)]" />
                <span className="text-stroke absolute -bottom-8 left-1/2 -translate-x-1/2 select-none whitespace-nowrap font-display text-[20vw] font-extrabold uppercase leading-none opacity-[0.35]">
                    Nexora
                </span>
            </div>

            <div className="relative mx-auto max-w-3xl px-6 text-center">
                <Reveal>
                    <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-cyan-300/80 md:text-xs">
                        03 — Limited first edition
                    </p>
                </Reveal>
                <Reveal delay={0.08}>
                    <h2 className="mt-6 font-display text-4xl font-extrabold uppercase leading-[0.95] tracking-tight text-white sm:text-5xl md:text-7xl">
                        Step into the
                        <br />
                        <span className="bg-gradient-to-r from-volt to-hyper bg-clip-text text-transparent">
                            sound future.
                        </span>
                    </h2>
                </Reveal>
                <Reveal delay={0.16}>
                    <p className="mx-auto mt-6 max-w-md text-sm font-light text-white/55 md:text-base">
                        Reserve your NEXORA with zero deposit. Worldwide
                        shipping begins Fall 2026.
                    </p>
                </Reveal>

                <Reveal delay={0.24}>
                    <form
                        onSubmit={onSubmit}
                        className="glass mx-auto mt-10 flex max-w-lg items-center gap-2 rounded-full p-2 pl-6"
                    >
                        <label htmlFor="nexora-email" className="sr-only">
                            Email address
                        </label>
                        <input
                            id="nexora-email"
                            data-testid="nexora-preorder-email-input"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="your@frequency.com"
                            className="w-full bg-transparent text-sm text-white placeholder:text-white/30 focus:outline-none"
                        />
                        <Magnetic strength={0.2}>
                            <button
                                type="submit"
                                data-testid="nexora-preorder-cta-btn"
                                className="flex shrink-0 items-center gap-2 rounded-full bg-volt px-6 py-3 text-sm font-semibold text-ink shadow-[0_0_28px_rgba(0,240,255,0.35)] transition-shadow duration-300 hover:shadow-[0_0_44px_rgba(0,240,255,0.6)]"
                            >
                                Reserve
                                <ArrowRight className="h-4 w-4" />
                            </button>
                        </Magnetic>
                    </form>
                </Reveal>
            </div>

            <footer className="relative mt-24 border-t border-white/5 md:mt-32">
                <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-6 px-6 py-8 md:flex-row md:px-10">
                    <Logo markClass="h-7 w-7" wordClass="text-sm" />
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/35">
                        © 2026 Nexora — a concept experience
                    </p>
                    <div className="flex items-center gap-3">
                        {[
                            { icon: Twitter, label: "Twitter" },
                            { icon: Instagram, label: "Instagram" },
                            { icon: Youtube, label: "YouTube" },
                        ].map(({ icon: Icon, label }) => (
                            <a
                                key={label}
                                href="#"
                                aria-label={`NEXORA on ${label}`}
                                onClick={(e) => e.preventDefault()}
                                className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 text-white/50 transition-all duration-300 hover:border-volt/60 hover:text-volt"
                            >
                                <Icon className="h-4 w-4" />
                            </a>
                        ))}
                    </div>
                </div>
            </footer>
        </section>
    );
}
